// Google reCAPTCHA v3 for Luminate Online donation forms.
//
// Luminate hands us the public key through getDonationFormInfo and expects the resulting
// token back in the `captcha_text` parameter of the donation call. The key is stored per
// campaign on the PaymentProvider, so a campaign whose Luminate form has no captcha
// element simply has no key and this module is never used.
// https://developer.blackbaud.com/lo-api/loapi/getting-started/reference/recaptcha

const SCRIPT_BASE = 'https://www.google.com/recaptcha/api.js?render='

const loadPromises = new Map()

const scriptUrlFor = (siteKey) => `${SCRIPT_BASE}${encodeURIComponent(siteKey)}`

/**
 * Loads the reCAPTCHA v3 library for a specific site key. Safe to call repeatedly.
 * Worth calling early — tokens themselves are short-lived, but the script isn't.
 *
 * @param {string} siteKey
 * @returns {Promise<void>}
 */
export function loadRecaptcha(siteKey) {
  if (!siteKey) return Promise.resolve()
  if (loadPromises.has(siteKey)) return loadPromises.get(siteKey)

  const promise = new Promise((resolve, reject) => {
    const src = scriptUrlFor(siteKey)
    const existing = document.querySelector(`script[src="${src}"]`)
    const fail = () => {
      loadPromises.delete(siteKey)
      reject(new Error('Failed to load Google reCAPTCHA.'))
    }

    if (existing) {
      if (window.grecaptcha?.execute) return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', fail)
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = fail
    document.head.appendChild(script)
  })

  loadPromises.set(siteKey, promise)
  return promise
}

/**
 * Produces a reCAPTCHA v3 token. Tokens expire after about two minutes, so call this
 * immediately before the donation request rather than up front.
 *
 * @param {string} siteKey
 * @param {string} [action] - Analytics label shown in the reCAPTCHA admin console.
 * @returns {Promise<string>} The token, or an empty string when no key is configured.
 */
export async function executeRecaptcha(siteKey, action = 'donate') {
  if (!siteKey) return ''

  await loadRecaptcha(siteKey)

  const grecaptcha = window.grecaptcha
  if (!grecaptcha?.execute) {
    throw new Error('Google reCAPTCHA loaded but is unavailable.')
  }

  await new Promise((resolve) => grecaptcha.ready(resolve))

  const token = await grecaptcha.execute(siteKey, { action })
  if (!token) {
    throw new Error('Google reCAPTCHA did not return a verification token.')
  }
  return token
}
